import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link } from "react-router-dom";
import { useAppDispatch } from "../../../redux/hooks/useTypedSelector";
import { resetAuth, signUpAction } from "../../../redux/slice/auth/authSlice";
import {
  SignUp as ISignUp,
  SignUpValidationSchema,
} from "../../../apps/auth/schema";
import { Field, FormAlert, PasswordField, SubmitButton } from "./fields";
import { authErrorMessage } from "./authErrorMessage";
import { AuthView } from "./AuthModalContext";

export const SignUpForm = ({
  onSwitch,
  onClose,
}: {
  onSwitch: (view: AuthView) => void;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState("");

  const {
    handleSubmit,
    register,
    setError: setFieldError,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(SignUpValidationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      accountType: "student",
    },
  });

  const signUp = async (data: ISignUp) => {
    setError("");
    if (data.password.length < 8) {
      setFieldError("password", {
        message: "Password must be at least 8 characters",
      });
      return;
    }
    const res = await dispatch(signUpAction(data));
    const payload = res.payload as { user?: unknown } | undefined;
    if (signUpAction.fulfilled.match(res) && payload?.user) {
      window.location.href = "/dashboard";
      return;
    }
    setError(
      authErrorMessage(
        res.payload,
        "We couldn't create your account. Please try again.",
      ),
    );
    dispatch(resetAuth());
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="font-headline-md text-headline-md text-on-surface">
          Create your free account
        </h2>
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Start practicing with a free mock test — no credit card needed.
        </p>
      </div>

      {error && <FormAlert tone="error">{error}</FormAlert>}

      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((data) => signUp(data as ISignUp))}
        noValidate
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="signup-first-name"
            label="First name"
            autoComplete="given-name"
            placeholder="First name"
            error={errors.firstName?.message}
            {...register("firstName")}
          />
          <Field
            id="signup-last-name"
            label="Last name"
            autoComplete="family-name"
            placeholder="Last name"
            error={errors.lastName?.message}
            {...register("lastName")}
          />
        </div>
        <Field
          id="signup-email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register("email")}
        />
        <PasswordField
          id="signup-password"
          label="Password"
          autoComplete="new-password"
          placeholder="At least 8 characters"
          error={errors.password?.message}
          {...register("password")}
        />
        <SubmitButton loading={isSubmitting}>Create Account</SubmitButton>
        <p className="font-body-sm text-xs text-on-surface-variant text-center">
          By creating an account you agree to our{" "}
          <Link
            to="/terms-conditions"
            onClick={onClose}
            className="text-primary hover:underline"
          >
            Terms &amp; Conditions
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy-policy"
            onClick={onClose}
            className="text-primary hover:underline"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </form>

      <p className="font-body-sm text-body-sm text-on-surface-variant text-center">
        Already have an account?{" "}
        <button
          type="button"
          onClick={() => onSwitch("signin")}
          className="font-label-md text-label-md text-primary hover:underline"
        >
          Sign in
        </button>
      </p>
    </div>
  );
};
